import { api, type FreeSetInfo } from '../sync/api';
import { $ } from './dom';

/*
 * หลังบ้าน → ตั้งค่า → "คลังกราฟิก — ชุดฟรี" (0019, choice ก): copies Microsoft Fluent Emoji and Phosphor Icons (MIT,
 * versions pinned in src/graphics/freeFiles.json) into the group's R2, one part (FREE_CHUNK files) per request, a few
 * parts at a time. D1 remembers the parts done: stopping (or the iPad sleeping) loses nothing — "ทำต่อ" goes on with
 * the parts left. A part with a file that failed is not counted done; the reason of every failure is shown.
 * When everything is done the files really in R2 are counted ("ตรวจความครบ"), so "เสร็จ" is never said on trust.
 */

const AT_ONCE = 3;
const errText = (e: unknown) => e instanceof Error ? e.message : String(e);
const nf = (n: number) => n.toLocaleString('th-TH');

export function initFreeImportCard() {
  const setsBox = $('fiSets'), prog = $('fiProg'), errBox = $('fiErr');
  const go = $('fiGo') as HTMLButtonElement, verifyBtn = $('fiVerify') as HTMLButtonElement, resetBtn = $('fiReset') as HTMLButtonElement;
  let sets: FreeSetInfo[] = [];
  let running = false, stop = false, armedReset = false;
  const errors: string[] = [];
  const checked = new Map<string, string>();   // set → the last "ตรวจความครบ" result

  const say = (box: HTMLElement, text: string, ok = false) => { box.textContent = text; box.hidden = !text; box.classList.toggle('ok', ok); };
  const left = () => sets.reduce((n, s) => n + (s.left?.length ?? 0), 0);

  function paint() {
    setsBox.textContent = '';
    for (const s of sets) {
      const row = document.createElement('div');
      row.className = 'firow';
      const b = document.createElement('b');
      b.textContent = `${s.name} (${s.license})`;
      const small = document.createElement('small');
      small.className = 'ufacts';
      small.textContent = `${s.ready ? '✓ นำเข้าครบแล้ว' : `นำเข้าแล้ว ${nf(s.done)} จาก ${nf(s.chunks)} ส่วน`} · ${nf(s.files)} ไฟล์ · ${s.catalog}`
        + (checked.get(s.set) ? ` · ${checked.get(s.set)}` : '');
      row.append(b, small);
      setsBox.append(row);
    }
    const n = left();
    go.textContent = running ? 'หยุด (ทำต่อทีหลังได้)' : n === 0 ? 'นำเข้าครบแล้ว' : sets.some(s => s.done) ? `ทำต่อ (เหลือ ${nf(n)} ส่วน)` : 'นำเข้าชุดฟรี';
    go.disabled = !running && n === 0;
    verifyBtn.disabled = running; resetBtn.disabled = running;
    resetBtn.textContent = armedReset ? 'แตะอีกครั้งเพื่อเริ่มนับใหม่' : 'ล้างแล้วเริ่มใหม่';
    errBox.textContent = errors.length ? `ไม่สำเร็จ ${errors.length} ส่วน:\n${errors.slice(-8).join('\n')}` : '';
    errBox.hidden = !errors.length;
  }

  async function load() {
    try { sets = (await api.freeStatus()).sets; }
    catch (e) { say(prog, `โหลดสถานะชุดฟรีไม่สำเร็จ: ${errText(e)}`); }
    paint();
  }

  async function run() {
    running = true; stop = false; errors.length = 0;
    say(prog, 'กำลังเริ่ม…', true); paint();
    const queue = sets.flatMap(s => (s.left ?? []).map(part => ({ set: s, part })));
    const total = queue.length;
    let done = 0;
    const worker = async () => {
      for (let job = queue.shift(); job && !stop; job = queue.shift()) {
        try {
          await api.freeImport(job.set.set, job.part);
          job.set.done++;
          job.set.left = job.set.left?.filter(p => p !== job.part);
          job.set.ready = job.set.done >= job.set.chunks;
        } catch (e) {
          const body = (e as { body?: { errors?: string[] } }).body;
          errors.push(`${job.set.name}: ${errText(e)}${body?.errors?.length ? ` — ${body.errors.slice(0, 2).join(' · ')}` : ''}`);
        }
        done++;
        say(prog, `กำลังนำเข้า ${nf(done)} จาก ${nf(total)} ส่วน (${Math.floor(done / total * 100)}%) — เปิดหน้านี้ค้างไว้ จอดับหรือปิดแอปแล้วกด "ทำต่อ" ได้`, true);
        paint();
      }
    };
    await Promise.all(Array.from({ length: AT_ONCE }, worker));
    running = false;
    await load();
    if (stop) say(prog, `หยุดแล้ว ทำไป ${nf(done)} ส่วน เหลือ ${nf(left())} ส่วน กด "ทำต่อ" เมื่อพร้อม`, true);
    else if (errors.length) say(prog, `เสร็จรอบนี้ แต่ไม่สำเร็จ ${nf(errors.length)} ส่วน (เหตุผลด้านล่าง) กด "ทำต่อ" เพื่อลองเฉพาะส่วนที่เหลือ`);
    else { say(prog, 'นำเข้าครบทุกส่วนแล้ว กำลังตรวจจำนวนไฟล์ในที่เก็บ…', true); await verify(); }
    paint();
  }

  /** the files really in R2 against the list — "เสร็จ" is said only when they match */
  async function verify() {
    const notes: string[] = [];
    let allOk = true;
    for (const s of sets) {
      try {
        const r = await api.freeVerify(s.set);
        const ok = r.found === r.expected;
        allOk &&= ok;
        checked.set(s.set, ok ? `ตรวจแล้ว ครบ ${nf(r.found)} ไฟล์` : `ตรวจแล้ว ขาด ${nf(r.expected - r.found)} ไฟล์`);
        notes.push(`${s.name}: ${ok ? `ครบ ${nf(r.found)} ไฟล์` : `มี ${nf(r.found)} จาก ${nf(r.expected)} ไฟล์ ขาด เช่น ${r.missing.slice(0, 3).join(', ')}`}`);
      } catch (e) { allOk = false; notes.push(`${s.name}: ตรวจไม่สำเร็จ — ${errText(e)}`); }
    }
    say(prog, notes.join('\n') + (allOk ? '' : '\nถ้าขาด: กด "ล้างแล้วเริ่มใหม่" แล้วนำเข้าอีกครั้ง (ไฟล์ที่มีแล้วจะถูกเขียนทับ)'), allOk);
    paint();
  }

  go.onclick = () => { if (running) { stop = true; go.textContent = 'กำลังหยุด…'; return; } void run(); };
  verifyBtn.onclick = () => { say(prog, 'กำลังตรวจ…', true); void verify(); };
  resetBtn.onclick = async () => {
    if (!armedReset) { armedReset = true; paint(); setTimeout(() => { armedReset = false; paint(); }, 4000); return; }
    armedReset = false;
    try {
      let n = 0;
      for (const s of sets) n += (await api.freeReset(s.set)).forgot;
      checked.clear();
      say(prog, `ล้างแล้ว (${nf(n)} ส่วน) กด "นำเข้าชุดฟรี" เพื่อเริ่มใหม่ ระหว่างนี้แผงองค์ประกอบจะยังไม่แสดงชุดฟรี`, true);
    } catch (e) { say(prog, `ล้างไม่สำเร็จ: ${errText(e)}`); }
    await load();
  };

  return { load };
}
